import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import { getPlatformHint, getRememberedPlatform } from 'modules/miniapp/detect'
import { faFull } from 'utilities/helper/format'

/**
 * What to do when nothing on this screen can take the money online.
 *
 * A member of a صندوق ثبت‌دستی could already *see* what they owed — that
 * shipped with the payables screen — but the row ended at «پرداخت نزد مدیر
 * صندوق», which tells someone what they cannot do and nothing about what they
 * can. This is the other half: the fund's card number to transfer to, who it
 * belongs to, and how to tell the manager afterwards.
 *
 * The card is only ever shown when the backend could name one without guessing
 * (the fund's own registered account, or an owner with exactly one verified
 * card). A wrong card number here is a real transfer into the wrong account,
 * so "no card" is a perfectly good answer and the phone still stands.
 */

export interface ManualPayment {
    name?: string
    phone?: string
    card?: string
    card_owner?: string
    messengers?: Record<string, string>
}

const MESSENGER_LABEL: Record<string, string> = {
    bale: 'بله',
    telegram: 'تلگرام',
    eitaa: 'ایتا',
}

const messengerLink = (platform: string, username: string) => {
    const handle = username.replace(/^@/, '')
    if (platform === 'bale') return `https://ble.ir/${handle}`
    if (platform === 'telegram') return `https://t.me/${handle}`
    if (platform === 'eitaa') return `https://eitaa.com/${handle}`
    return ''
}

/** Group a PAN as 4-4-4-4 without changing a digit of it. */
const groupCard = (card: string) =>
    card.replace(/\D/g, '').replace(/(.{4})(?=.)/g, '$1-')

/**
 * WHY this screen cannot take the money.
 *
 * - `no_gateway` — the fund has no online payment at all (صندوق ثبت‌دستی).
 * - `manager_only` — the fund DOES have a gateway, but on a «تک‌پرداختی» fund
 *   it belongs to the manager. Saying «این صندوق پرداخت آنلاین ندارد» to a
 *   member whose manager pays online every week is simply untrue, and it is
 *   what the fund page contradicted out loud (pwa#30).
 */
export type ManualPaymentReason = 'no_gateway' | 'manager_only'

const REASON_COPY: Record<ManualPaymentReason, string> = {
    no_gateway: 'این صندوق پرداخت آنلاین ندارد.',
    manager_only: 'پرداخت آنلاین این صندوق فقط برای مدیر صندوق فعال است.',
}

export default function ManualPaymentCard({
    info,
    boxName,
    amount,
    source,
    reason = 'no_gateway',
    viewerIsManager = false,
}: {
    info?: ManualPayment
    boxName?: string
    /** what is owed, so the copy can name it */
    amount?: number
    /** which screen this rendered on, for analytics */
    source: string
    reason?: ManualPaymentReason
    /**
     * The caller is a manager (`mgr`/`own`) of this box, and therefore the
     * person the card would otherwise tell them to go and contact.
     */
    viewerIsManager?: boolean
}) {
    const [copied, setCopied] = useState(false)
    const card = info?.card?.replace(/\D/g, '') ?? ''
    const phone = info?.phone ?? ''

    // Only offer the messenger route inside that messenger: `ble.ir/<user>`
    // opens the app, and in a browser on a desktop that is a worse answer than
    // the phone number the user already has.
    const platform = getRememberedPlatform() ?? getPlatformHint()
    const handle = platform ? info?.messengers?.[platform] ?? '' : ''
    const link = platform && handle ? messengerLink(platform, handle) : ''

    // How often this card is the answer, and how complete the answer was. The
    // fund's name is deliberately absent: it is text a manager typed.
    useEffect(() => {
        posthog.capture('manual_payment_shown', {
            source,
            reason,
            amount: amount ?? null,
            viewer_is_manager: viewerIsManager,
            has_card: !!card,
            has_phone: !!phone,
            has_messenger: !!link,
            platform: platform ?? null,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const copyCard = () => {
        // `navigator.clipboard` is undefined outside a secure context — plain
        // http:// and a few older Bale/Eitaa webviews. The optional chain then
        // leaves `undefined.then(...)`, which throws a TypeError *before*
        // `.catch()` exists to turn it into the toast below.
        if (!navigator.clipboard) {
            toast.info('شماره کارت را دستی یادداشت کنید')
            return
        }
        navigator.clipboard
            .writeText(card)
            .then(() => {
                setCopied(true)
                posthog.capture('manual_payment_card_copied', { source })
                setTimeout(() => setCopied(false), 2000)
            })
            .catch(() => toast.info('شماره کارت را دستی یادداشت کنید'))
    }

    // Everything above is a hook or derived from one, so the manager branch
    // below is safe to return early from.
    if (viewerIsManager) {
        // «پیام به مدیر صندوق در بله» and «تماس با مدیر صندوق (جابر آزور)»
        // rendered for جابر آزور is the app sending someone to find
        // themselves, and the card number is the manager's own fund's. What
        // is left worth saying is why there is no gateway on the screen —
        // «ثبت پرداخت» is already right below this.
        return (
            <div className="rounded-card border border-divider bg-sunken p-3">
                <p className="text-[13px] leading-6 text-text-secondary">
                    {REASON_COPY[reason]} مبلغی را که دستی دریافت کرده‌اید،
                    همین‌جا ثبت کنید.
                </p>
            </div>
        )
    }

    return (
        <div className="bg-white border border-divider rounded-card p-4">
            {/* Named after the fund, not after the mode. The group above this
                card is already headed «پرداخت نزد مدیر صندوق»; repeating it
                here read as a second, separate instruction. */}
            <p className="text-sm font-bold text-heading">
                {boxName ? `پرداخت به صندوق «${boxName}»` : 'پرداخت دستی'}
            </p>
            <p className="mt-1 text-[13px] leading-6 text-text-secondary">
                {REASON_COPY[reason]}{' '}
                {card
                    ? `${
                          amount
                              ? `مبلغ ${faFull.format(amount)} تومان را`
                              : 'مبلغ را'
                      } به این کارت واریز کنید و بعد به مدیر صندوق خبر بدهید.`
                    : 'برای پرداخت با مدیر صندوق هماهنگ کنید.'}
            </p>

            {card && (
                <div className="mt-3 rounded-card bg-sunken p-3">
                    <p className="text-[11px] text-text-secondary">
                        شماره کارت صندوق
                    </p>
                    {/* dir=ltr: a PAN is a left-to-right run and the bidi
                        algorithm otherwise reorders the dash-separated groups. */}
                    <p
                        dir="ltr"
                        className="mt-1 text-left text-[17px] font-bold tracking-wide text-heading"
                    >
                        {groupCard(card)}
                    </p>
                    {info?.card_owner && (
                        <p className="mt-1 text-[12px] text-text-secondary">
                            به نام {info.card_owner}
                        </p>
                    )}
                    <button
                        type="button"
                        onClick={copyCard}
                        className="mt-2 w-full rounded-full border border-green-1 bg-white py-2 text-sm font-bold text-green-1 active:scale-[0.99] transition-transform motion-reduce:transition-none"
                    >
                        {copied ? 'کپی شد ✓' : 'کپی شماره کارت'}
                    </button>
                </div>
            )}

            <div className="mt-3 flex flex-col gap-2">
                {link && (
                    <a
                        href={link}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() =>
                            posthog.capture('manual_payment_messenger_opened', {
                                source,
                                platform,
                            })
                        }
                        className="w-full rounded-full bg-green-1 py-2.5 text-center text-[15px] font-bold text-white active:scale-[0.99] transition-transform motion-reduce:transition-none"
                    >
                        پیام به مدیر صندوق در {MESSENGER_LABEL[platform!]}
                    </a>
                )}
                {phone && (
                    <a
                        href={`tel:${phone}`}
                        onClick={() =>
                            posthog.capture('manual_payment_manager_called', {
                                source,
                            })
                        }
                        className={`w-full rounded-full py-2.5 text-center text-[15px] font-bold active:scale-[0.99] transition-transform motion-reduce:transition-none ${
                            link
                                ? 'border border-green-1 bg-white text-green-1'
                                : 'bg-green-1 text-white'
                        }`}
                    >
                        تماس با مدیر صندوق
                        {info?.name ? ` (${info.name})` : ''}
                    </a>
                )}
            </div>

            {!card && !phone && (
                <p className="mt-3 text-[13px] text-text-muted">
                    اطلاعات تماس مدیر این صندوق ثبت نشده است.
                </p>
            )}
        </div>
    )
}
